import React, { useEffect, useMemo, useRef, useState } from 'react';
import { restaurantCategories } from '../data/restaurants';
import { attractionCategories } from '../data/attractions';
import { AROUND_SPOTS } from './TheHouse';
import { CATEGORY_WORDS } from '../data/searchWords';
import { neighborhoodCategories } from '../data/neighborhoods';
import { monthlyEvents, EventTag } from '../data/monthlyEvents';
import { requestEvent, EventRequest } from '../utils/eventLink';

// Mobile navigation: a floating liquid-glass tab bar for the main chapters,
// with a search button beside it. The tab for the chapter in view is
// highlighted. Search opens a glass panel above the bar: shortcuts and the
// chapter list before typing, then matching guide info, restaurants and
// things to do. Desktop gets just the search, as a pill in the corner.

export interface GuideChapter {
  id: string;
  label: string;
}

export const GUIDE_CHAPTERS: GuideChapter[] = [
  { id: 'hero', label: 'Welcome' },
  { id: 'stay-guide', label: 'Your stay' },
  { id: 'the-house', label: 'Around here' },
  { id: 'dining', label: 'Restaurants' },
  { id: 'neighborhoods-explore', label: 'Explore' },
  { id: 'climate', label: 'Seasons & events' },
  { id: 'essentials', label: 'Essentials' },
];

type IconName = 'stay' | 'around' | 'eat' | 'explore' | 'seasons' | 'essentials';

// Short labels for the phone tab bar. Seasons & events is left off so the bar
// and the search button fit; it's in the chapter list when search opens.
const TABS: { id: string; short: string; icon: IconName }[] = [
  { id: 'stay-guide', short: 'Stay', icon: 'stay' },
  { id: 'the-house', short: 'Around', icon: 'around' },
  { id: 'dining', short: 'Eat', icon: 'eat' },
  { id: 'neighborhoods-explore', short: 'Explore', icon: 'explore' },
  { id: 'essentials', short: 'Info', icon: 'essentials' },
];

const TabIcon: React.FC<{ name: IconName }> = ({ name }) => {
  const common = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  };
  switch (name) {
    case 'stay':
      return (
        <svg {...common}>
          <path d="M4 18c3-6 6-9 8-9s5 3 8 9" />
          <circle cx="12" cy="6" r="2" />
        </svg>
      );
    case 'around':
      return (
        <svg {...common}>
          <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
          <circle cx="12" cy="9.5" r="2.4" />
        </svg>
      );
    case 'eat':
      return (
        <svg {...common}>
          <path d="M7 3v8M5 3v5a2 2 0 0 0 4 0V3M7 11v10M16 3c-2 1-3 4-3 7h3v11" />
        </svg>
      );
    case 'explore':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="m15.5 8.5-2 5-5 2 2-5z" />
        </svg>
      );
    case 'seasons':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
        </svg>
      );
    case 'essentials':
    default:
      return (
        <svg {...common}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M9 8h6M9 12h6M9 16h3" />
        </svg>
      );
  }
};

// ── Search ────────────────────────────────────────────────────────────────
// Guide info (anchors into Essentials and the stay route), restaurants and
// things to do. Places jump to their card, the same way "Around here" does.

type InfoItem = { label: string; detail: string; href: string; words?: string };

const INFO: InfoItem[] = [
  { label: 'Wi-Fi', detail: 'Connect when you arrive', href: '#wifi', words: 'wifi internet password network' },
  { label: 'Parking', detail: 'Your dedicated spot', href: '#parking', words: 'car garage spot' },
  { label: 'Arrival & smart lock', detail: 'Check-in after 4 PM', href: '#arrival-checkin', words: 'check-in checkin door code keypad lock' },
  { label: 'Checkout steps', detail: 'Before you head out', href: '#house-rules', words: 'check-out checkout leaving rules' },
  { label: 'Getting around', detail: 'Walk, rail, and rideshare', href: '#getting-around', words: 'transit bus light rail uber lyft' },
  { label: 'Getting to the airport', detail: 'Light rail and rideshare', href: '#getting-around', words: 'sea-tac seatac airport flight' },
  { label: 'Inside the house', detail: 'Rooftop, kitchen & more', href: '#inside-the-house', words: 'rooftop kitchen bedroom floors' },
];

type Place = { kind: 'restaurant' | 'attraction'; name: string; meta: string; tags: string; text: string };

// Search words for a place: its own tags plus its category's.
const tagsFor = (category: string, own?: string[]) => [...(own ?? []), ...(CATEGORY_WORDS[category] ?? [])].join(' ');

const PLACES: Place[] = [
  ...restaurantCategories.flatMap((c) =>
    c.restaurants.map((r) => ({
      kind: 'restaurant' as const,
      name: r.name,
      meta: c.title,
      tags: tagsFor(c.title, r.tags),
      text: `${r.description} ${r.tip ?? ''}`,
    })),
  ),
  ...attractionCategories.flatMap((c) =>
    c.attractions.map((a) => ({
      kind: 'attraction' as const,
      name: a.name,
      meta: c.title,
      tags: tagsFor(c.title, a.tags),
      text: `${a.description} ${a.proTip ?? ''}`,
    })),
  ),
];

// Everyday spots from "Around here", with the words guests actually type.
const AROUND_WORDS: Record<string, string> = {
  Groceries: 'grocery groceries supermarket store food market shopping',
  'Playground & park': 'park playground kids children green',
  'Campus walks': 'university college campus walk',
  'Bus · RapidRide G': 'bus transit metro rapidride downtown',
  'Scooters & e-bikes': 'scooter bike ebike lime',
  'Light rail': 'train link rail subway transit airport sea-tac seatac',
};
type Spot = { name: string; meta: string; tags: string };
const SPOTS: Spot[] = AROUND_SPOTS.map((s) => ({
  name: s.name,
  meta: `${s.kind} · ${s.value} ${s.unit === 'mi' ? 'mi' : 'min walk'}`,
  tags: AROUND_WORDS[s.kind] ?? '',
}));

// Explore Seattle neighborhoods ("fremont", "pike place", "ballard" …).
type Area = { id: string; name: string; meta: string; text: string };
const AREAS: Area[] = neighborhoodCategories.flatMap((c) =>
  c.neighborhoods.map((n) => ({
    id: n.id,
    name: n.name,
    meta: n.id === 'capitol-hill' ? 'Neighborhood · you’re here' : 'Neighborhood',
    text: `neighborhood area ${n.description} ${n.firstStop.name}`,
  })),
);

// Seasons & events. An event that repeats (Art Walks, Kraken games) is one
// result that says when it runs, and opens on the month nearest to now.
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TAG_WORDS: Record<EventTag, string> = {
  free: 'free',
  food: 'food',
  nature: 'nature outdoors',
  sports: 'sports game',
  arts: 'arts art',
  family: 'family kids',
  festival: 'festival',
  market: 'farmers market',
};

// "Every month", "Oct – Feb" (one run, even across the new year), or "Apr, Oct".
const whenLabel = (months: number[]): string => {
  const on = new Set(months);
  if (on.size === 12) return 'Every month';
  const next = (m: number) => (m % 12) + 1;
  const prev = (m: number) => ((m + 10) % 12) + 1;
  const starts = Array.from(on).filter((m) => !on.has(prev(m))).sort((a, b) => a - b);
  const runs = starts.map((start) => {
    let end = start;
    while (on.has(next(end))) end = next(end);
    return end === start ? SHORT_MONTH[start - 1] : `${SHORT_MONTH[start - 1]} – ${SHORT_MONTH[end - 1]}`;
  });
  return runs.join(', ');
};

type SeasonEvent = { title: string; label: string; months: number[]; text: string };
const EVENTS: SeasonEvent[] = (() => {
  const byTitle = new Map<string, SeasonEvent>();
  monthlyEvents.forEach((m) =>
    m.events.forEach((e) => {
      const entry = byTitle.get(e.title);
      if (entry) {
        entry.months.push(m.month);
        entry.text += ` ${m.name}`;
        return;
      }
      byTitle.set(e.title, {
        title: e.title,
        label: e.title.replace(/:\s*$/, ''),
        months: [m.month],
        text: `${e.description} ${e.tags.map((t) => TAG_WORDS[t]).join(' ')} ${m.name}`,
      });
    }),
  );
  return Array.from(byTitle.values());
})();

// This month if it's on now, otherwise the next month it happens.
const nearestMonth = (months: number[]): number => {
  const now = new Date().getMonth() + 1;
  for (let i = 0; i < 12; i += 1) {
    const m = ((now - 1 + i) % 12) + 1;
    if (months.includes(m)) return m;
  }
  return months[0];
};

export const TOP_HITS = 3;
// Lower-case, no accents or apostrophes ("Trader Joe's" → "trader joes").
const fold = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/['\u2019]/g, '').toLowerCase();
// Words with a plural "s" dropped, so "trader's joe" finds "Trader Joe's"
// and "bus" finds "Bus · RapidRide G".
const words = (s: string) =>
  fold(s)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((w) => (w.length > 3 ? w.replace(/s$/, '') : w));
// Every word typed has to start a word in the field (in any order).
const matches = (q: string[], field: string, whole = false) => {
  const ws = words(field);
  return q.every((t) => ws.some((w) => (whole ? w === t : w.startsWith(t))));
};

// Whole words beat partial ones ("bus" → the bus stop before "Busan Jeong");
// within each: the name, then the category, then the search tags, then the
// description.
const rank = (q: string[], name: string, meta: string, tags: string, text = ''): number => {
  const fields = [name, meta, tags, `${name} ${meta} ${tags} ${text}`];
  const whole = fields.findIndex((f) => matches(q, f, true));
  if (whole >= 0) return whole;
  const part = fields.findIndex((f) => matches(q, f));
  return part >= 0 ? fields.length + part : -1;
};

export type Hit = { key: string; label: string; meta: string; href?: string; place?: Place; spot?: string; area?: string; event?: EventRequest; r: number };

// One list, best matches first; ties keep the order guide info → nearby →
// neighborhoods → restaurants → sights → months → events.
export const searchGuide = (raw: string): Hit[] | null => {
  if (fold(raw.trim()).length < 2) return null;
  const q = words(raw);
  if (!q.length) return [];
  const hits: Hit[] = [
    ...INFO.map((i) => ({ key: `info-${i.label}`, label: i.label, meta: i.detail, href: i.href, r: rank(q, i.label, i.detail, i.words ?? '') })),
    ...SPOTS.map((s) => ({ key: `near-${s.name}`, label: s.name, meta: s.meta, spot: s.name, r: rank(q, s.name, s.meta, s.tags) })),
    ...AREAS.map((a) => ({ key: `area-${a.id}`, label: a.name, meta: a.meta, area: a.id, r: rank(q, a.name, a.meta, '', a.text) })),
    ...PLACES.map((p) => ({ key: `${p.kind}-${p.name}`, label: p.name, meta: p.meta, place: p, r: rank(q, p.name, p.meta, p.tags, p.text) })),
    ...monthlyEvents.map((m) => ({
      key: `month-${m.month}`,
      label: `What's on in ${m.name}`,
      meta: m.theme,
      event: { month: m.month },
      r: rank(q, `${m.name} ${SHORT_MONTH[m.month - 1]}`, m.theme, 'events whats on'),
    })),
    ...EVENTS.map((e) => ({
      key: `event-${e.title}`,
      label: e.label,
      meta: `Event · ${whenLabel(e.months)}`,
      event: { month: nearestMonth(e.months), title: e.title },
      r: rank(q, e.label, 'event', '', e.text),
    })),
  ];
  return hits.filter((h) => h.r >= 0).sort((a, b) => a.r - b.r);
};

// Open a result that isn't a plain link (the hero search shares this).
export const openHit = (h: Hit) => {
  if (h.spot) {
    // Nearby spots live in "Around here": open that chapter and bring the row into view.
    const name = h.spot;
    window.dispatchEvent(new CustomEvent('corazon:open-chapter', { detail: { id: 'the-house' } }));
    window.setTimeout(() => {
      const row = document.querySelector<HTMLElement>(`[data-around-spot="${CSS.escape(name)}"]`);
      if (!row) return;
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('is-found');
      window.setTimeout(() => row.classList.remove('is-found'), 1800);
    }, 350);
  } else if (h.area) {
    window.dispatchEvent(new CustomEvent('corazon:open-area', { detail: { id: h.area } }));
  } else if (h.event) {
    requestEvent(h.event);
  } else if (h.place) {
    const event = h.place.kind === 'restaurant' ? 'corazon:open-restaurant' : 'corazon:open-attraction';
    window.dispatchEvent(new CustomEvent(event, { detail: { name: h.place.name } }));
  }
};

export const GlassSearchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

const GuideSheet: React.FC = () => {
  const [current, setCurrent] = useState(GUIDE_CHAPTERS[0].id);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  // Height of the on-screen keyboard, so the search card sits right above it.
  const [keyboard, setKeyboard] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => searchGuide(query), [query]);

  // Track the chapter in view: the last one whose top has passed a third of the screen.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.scrollY + window.innerHeight / 3;
      let next = GUIDE_CHAPTERS[0].id;
      for (const chapter of GUIDE_CHAPTERS) {
        const el = document.getElementById(chapter.id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= line) next = chapter.id;
      }
      setCurrent(next);
    };
    const schedule = () => {
      if (frame) return;
      if (typeof window.requestAnimationFrame === 'function') frame = window.requestAnimationFrame(update);
      else update();
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      if (frame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
    };
  }, []);

  // "/" or ⌘K / Ctrl+K opens search from anywhere (handy on desktop),
  // unless the guest is already typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
      const slash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if ((slash && !typing) || cmdK) {
        e.preventDefault();
        // The hero has its own search bar; use it while it's on screen.
        const hero = document.querySelector<HTMLInputElement>('.ed-ask input');
        if (hero && hero.getBoundingClientRect().bottom > 0) hero.focus();
        else setSearching(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The hero search hands off here on phones, and for "See all".
  useEffect(() => {
    const open = (e: Event) => {
      const { query: q = '', all = false } = (e as CustomEvent<{ query?: string; all?: boolean }>).detail ?? {};
      setQuery(q);
      setShowAll(all);
      setSearching(true);
    };
    window.addEventListener('corazon:open-search', open);
    return () => window.removeEventListener('corazon:open-search', open);
  }, []);

  // Focus the field when search opens; Escape closes it.
  useEffect(() => {
    if (!searching) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearching(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searching]);

  const closeSearch = () => {
    setSearching(false);
    setQuery('');
    setShowAll(false);
  };

  useEffect(() => {
    const vv = window.visualViewport;
    if (!searching || !vv) return;
    const update = () => setKeyboard(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setKeyboard(0);
    };
  }, [searching]);

  const goTo = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    setCurrent(id);
    closeSearch();
    // Phones fold long chapters; open this one before jumping to it.
    window.dispatchEvent(new CustomEvent('corazon:open-chapter', { detail: { id } }));
    const el = document.getElementById(id);
    const anchor = (el?.closest('.chapter-fold') as HTMLElement | null) ?? el;
    if (anchor) window.scrollTo({ top: anchor.offsetTop, behavior: 'smooth' });
  };

  const hits: Hit[] = results ?? [];
  const shown = showAll ? hits : hits.slice(0, TOP_HITS);

  return (
    <>
      {searching && (
        <div className="guide-search" role="dialog" aria-modal="true" aria-label="Search the guide">
          <button type="button" className="guide-search-dim" aria-label="Close search" tabIndex={-1} onClick={closeSearch} />
          <div
            className={`guide-search-panel ${showAll ? 'is-all' : ''}`}
            style={keyboard > 0 ? { bottom: keyboard + 8, maxHeight: `calc(100dvh - ${keyboard + 24}px)` } : undefined}
          >
            {!results ? null : hits.length === 0 ? (
              <p className="guide-search-empty" aria-live="polite">No matches for “{query.trim()}”. Try “coffee”, “park” or “Wi-Fi”.</p>
            ) : (
              <div className="guide-search-results" aria-live="polite">
                <ul>
                  {shown.map((h) => (
                    <li key={h.key}>
                      {h.href ? (
                        <a href={h.href} onClick={closeSearch}>
                          <strong>{h.label}</strong>
                          <small>{h.meta}</small>
                        </a>
                      ) : (
                        <button type="button" onClick={() => {
                            closeSearch();
                            openHit(h);
                          }}>
                          <strong>{h.label}</strong>
                          <small>{h.meta}</small>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {!showAll && hits.length > TOP_HITS && (
                  <button
                    type="button"
                    className="guide-search-more"
                    onClick={() => {
                      setShowAll(true);
                      inputRef.current?.blur(); // let the keyboard go so the full list has room
                    }}
                  >
                    See all {hits.length} results
                  </button>
                )}
              </div>
            )}
            <div className="guide-search-row">
              <label className="guide-search-field">
                <GlassSearchIcon />
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowAll(false);
                  }}
                  placeholder="Search places, Wi-Fi, check-in…"
                  aria-label="Search the guide"
                  enterKeyHint="search"
                />
              </label>
              <button type="button" className="guide-search-close" aria-label="Close search" onClick={closeSearch}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {!searching && (
        <div className={`guide-dock ${current === 'hero' ? 'is-hero' : ''}`}>
          <nav className="guide-tabs" aria-label="Jump to a section">
            {TABS.map((tab) => {
              const isCurrent = tab.id === current;
              const label = GUIDE_CHAPTERS.find((c) => c.id === tab.id)?.label ?? tab.short;
              return (
                <a
                  key={tab.id}
                  href={`#${tab.id}`}
                  className={isCurrent ? 'is-current' : ''}
                  aria-current={isCurrent ? 'location' : undefined}
                  aria-label={label}
                  onClick={(e) => goTo(e, tab.id)}
                >
                  <TabIcon name={tab.icon} />
                  <span aria-hidden="true">{tab.short}</span>
                </a>
              );
            })}
          </nav>
          <button type="button" className="guide-search-btn" aria-label="Search the guide" aria-expanded={false} onClick={() => setSearching(true)}>
            <GlassSearchIcon />
            <span className="guide-search-label" aria-hidden="true">
              Search <kbd>/</kbd>
            </span>
          </button>
        </div>
      )}
    </>
  );
};

export default GuideSheet;
